import { onDestroy, onMount } from 'svelte';
import { get } from 'svelte/store';
import { socketManager, isConnected } from '../socketManager';
import { storage } from '../storage';

interface UseSocketOptions {
  componentName?: string;
  autoConnect?: boolean;
}

interface SocketEvent {
  event: string;
  handler: (...args: any[]) => void;
}

export function useSocket(options: UseSocketOptions = {}) {
  const { componentName = 'unknown', autoConnect = false } = options;
  const cleanupFunctions: Array<() => void> = [];

  onMount(async () => {
    if (autoConnect && !get(isConnected)) {
      // Check if user is authenticated before auto-connecting
      const token = await storage.get<string>('authJwt');
      if (token) {
        console.log(`[${componentName}] Auto-connecting socket with valid token`);
        socketManager.connect();
      } else {
        console.log(`[${componentName}] No auth token found, skipping auto-connect`);
      }
    }
  });

  onDestroy(() => {
    // Clean up all event listeners
    cleanupFunctions.forEach(cleanup => cleanup());
    cleanupFunctions.length = 0;
  });

  return {
    // Subscribe to socket events
    on(event: string, handler: (...args: any[]) => void) {
      const cleanup = socketManager.on(event, handler, componentName);
      cleanupFunctions.push(cleanup);
      return cleanup;
    },

    // Emit socket events
    emit(event: string, data?: any) {
      socketManager.emit(event, data);
    },

    // Subscribe to multiple events at once
    onMany(events: SocketEvent[]) {
      events.forEach(({ event, handler }) => {
        const cleanup = socketManager.on(event, handler, componentName);
        cleanupFunctions.push(cleanup);
      });
    },

    // Get socket status
    get status() {
      return socketManager.status;
    },

    // Get connection state
    get isConnected() {
      return socketManager.isConnected;
    },

    // Manual connection control
    connect() {
      return socketManager.connect();
    },

    disconnect() {
      socketManager.disconnect();
    },

    reconnect() {
      socketManager.reconnect();
    },

    // Wait for connection
    waitForConnection(timeout?: number) {
      return socketManager.waitForConnection(timeout);
    },
  };
}

// Example usage in a Svelte component:
/*
<script lang="ts">
  import { useSocket } from '$lib/hooks/useSocket';
  
  const socket = useSocket({ componentName: 'ChatScreen' });
  
  // Subscribe to events
  socket.on('message', (message) => {
    console.log('New message:', message);
  });
  
  socket.on('typing', (data) => {
    console.log('User typing:', data);
  });
  
  // Emit events
  function sendMessage(text: string) {
    socket.emit('message', { text });
  }
  
  // Check connection status
  $: if ($socket.isConnected) {
    console.log('Connected!');
  }
</script>
*/